import React, { FC, PropsWithChildren } from 'react'
import { Stack, Typography, Paper, Avatar, useTheme } from '@mui/material'
import Button from '@components/Button'
import { HiChevronDoubleRight } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import TokenAvatar from '@components/TokenAvatar'
import { getChainIcon } from '@plug-core/utils/getChainIcon'
import { Chain } from '@plug-core/interfaces/common'
import { formatNumber } from '@plug-core/utils/formatNumber'

export type ExchangeViewType = 'swap' | 'convert' | 'mint' | 'bridge'

interface Props extends PropsWithChildren {
  fromTokenSymbol?: string
  toTokenSymbol?: string
  fromTokenImage?: string
  toTokenImage?: string
  fromTokenChain?: Chain
  toTokenChain?: Chain
  amount?: string
  toAmount?: string
  buttonTitle: string
  handleSubmit: () => void
  loading: boolean
  kind: ExchangeViewType
  error?: string
  step?: string
}

const ExchangeReview: FC<Props> = ({
  children,
  fromTokenSymbol,
  toTokenSymbol,
  fromTokenImage,
  toTokenImage,
  fromTokenChain,
  toTokenChain,
  amount,
  toAmount,
  buttonTitle,
  handleSubmit,
  loading,
  error,
  kind,
  step,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const { t } = useTranslation()

  const renderArrowView = () => {
    return (
      <Stack
        position="absolute"
        justifyContent="center"
        p={1}
        bgcolor={(theme) => theme.palette.background.default}
        borderRadius={10}
      >
        <HiChevronDoubleRight color={theme.palette.primary.main} size={30} />
      </Stack>
    )
  }

  return (
    <Stack>
      <Typography variant="h6" textAlign="center" mt={2}>
        Review {t(`common.${kind}`)}
      </Typography>
      <Stack px={2} py={2} spacing={1}>
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Stack component={Paper} flex={1} alignItems="center" spacing={1.5} p={2} height="100%">
            <Stack position="relative">
              <TokenAvatar size={44} logo={fromTokenImage} />
              {fromTokenChain && (
                <Stack
                  position="absolute"
                  right={-2}
                  bottom={-6}
                  bgcolor="background.paper"
                  padding={0.45}
                  borderRadius={4}
                >
                  <img src={getChainIcon(fromTokenChain)} height={15} width={15} />
                </Stack>
              )}
            </Stack>
            <Stack spacing={0.5}>
              <Typography variant="h3" textAlign="center" width={100} noWrap>
                {amount}{' '}
              </Typography>
              <Typography variant="caption" textAlign="center">
                {fromTokenSymbol}
              </Typography>
            </Stack>
          </Stack>
          {renderArrowView()}
          <Stack
            component={Paper}
            flex={1}
            alignItems="center"
            spacing={1.5}
            p={2}
            ml={1}
            height="100%"
          >
            <Stack position="relative">
              <TokenAvatar size={44} logo={toTokenImage} />
              {toTokenChain && (
                <Stack
                  position="absolute"
                  right={-2}
                  bottom={-6}
                  bgcolor="background.paper"
                  padding={0.45}
                  borderRadius={4}
                >
                  <img src={getChainIcon(toTokenChain)} height={15} width={15} />
                </Stack>
              )}
            </Stack>
            <Stack spacing={0.5}>
              <Typography variant="h3" textAlign="center" width={100} noWrap>
                {toAmount}
              </Typography>
              <Typography variant="caption" textAlign="center">
                {toTokenSymbol}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        {kind === 'mint' && (
          <Typography variant="body2" color="textSecondary" textAlign="center" mt={1}>
            {t('mintCk.delayWarning')}
          </Typography>
        )}
        {kind === 'bridge' && (
          <Typography variant="body2" color="textSecondary" textAlign="center" mt={1}>
            {t('onesec.delayWarning')}
          </Typography>
        )}
        {children}
      </Stack>
      <Stack bgcolor={(theme) => theme.palette.background.mid} px={2} py={2} spacing={0.5}>
        <Button
          variant="rainbow"
          loading={loading}
          disabled={!!error || loading}
          onClick={handleSubmit}
          fullWidth
        >
          {step || buttonTitle}
        </Button>
        <Button onClick={() => navigate(-1)} disabled={loading} fullWidth>
          Cancel
        </Button>
      </Stack>
    </Stack>
  )
}

export default ExchangeReview
